import Notification from '@rio-cloud/rio-uikit/Notification';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router';

import { useCustomerContract, useUpdateContract } from '@/api/account';
import { extractLanguage } from '@/configuration/lang/lang';
import { CompanyContractData } from '@/features/administration/contract/components/CompanyContractData';
import { ContactDetails } from '@/features/administration/contract/components/ContactDetails';
import { Documents } from '@/features/administration/contract/components/Documents';
import { InvoiceDetails } from '@/features/administration/contract/components/InvoiceDetails';
import { PendingChangesModal } from '@/features/administration/contract/components/PendingChangesModal';
import { useTabAnalytics } from '@/features/administration/contract/hooks/useTabAnalytics';
import type { ContactDetailsSchemaType } from '@/features/administration/contract/validations/contact';
import type { InvoiceDetailsSchemaType } from '@/features/administration/contract/validations/invoice';
import SupportModal from '@/features/chargingActivity/components/SupportModal';
import {
    MAN_CHARGE_AND_GO,
    PRIVACY_POLICY_URL,
    RIO_PRICE_LIST_URL,
    TCS,
    TCS_PRIVACY_POLICY_URL,
} from '@/lib/constants';
import { formatUrl, parseCountryAndPhoneNumber, t } from '@/lib/utils';
import { useUserStore } from '@/stores/userStore';

const TAB_KEYS = ['company', 'contact', 'invoicing', 'documents'] as const;
type TabKey = (typeof TAB_KEYS)[number];

const isTabKey = (value: string | null): value is TabKey =>
    value !== null && (TAB_KEYS as readonly string[]).includes(value);

const ChargingContract = () => {
    const intl = useIntl();
    const [dialog, setDialog] = useState<boolean>(false);
    const [searchParams, setSearchParams] = useSearchParams();

    const tabSelected = useMemo(() => {
        const tabParam = searchParams.get('tab');
        return isTabKey(tabParam) ? TAB_KEYS.indexOf(tabParam) : 0;
    }, [searchParams]);

    const [lastTabSelected, setLastTabSelected] = useState<number | undefined>(undefined);

    const { handleTabChange } = useTabAnalytics();

    const updateTabInUrl = useCallback(
        (index: number) => {
            setSearchParams(
                (prev: URLSearchParams) => {
                    const next = new URLSearchParams(prev);
                    next.set('tab', TAB_KEYS[index]);
                    return next;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    // Ensure the URL always reflects a valid tab (e.g. on first load without ?tab=...).
    useEffect(() => {
        const tabParam = searchParams.get('tab');
        if (!isTabKey(tabParam)) {
            updateTabInUrl(0);
        }
    }, [searchParams, updateTabInUrl]);

    const [contactData, setContactData] = useState<ContactDetailsSchemaType | undefined>();
    const [invoiceData, setInvoiceData] = useState<InvoiceDetailsSchemaType | undefined>();

    const contactFormRef = useRef<UseFormReturn<ContactDetailsSchemaType> | null>(null);
    const invoiceFormRef = useRef<UseFormReturn<InvoiceDetailsSchemaType> | null>(null);

    const { account } = useUserStore();

    const userLanguage = extractLanguage(intl.locale);

    const permissions = account?.permissions?.includes('emsp-contract.write');

    const { data: contract, isLoading: isContractLoading } = useCustomerContract();
    const updateContractMutation = useUpdateContract();

    useEffect(() => {
        if (contract) {
            const parsedPhoneNumber = parseCountryAndPhoneNumber(contract.contactPhoneNumber);
            setContactData({
                email: contract.contactEmail || '',
                countryCode: parsedPhoneNumber ? parsedPhoneNumber.country : contract.countryCode || '',
                phone: parsedPhoneNumber ? parsedPhoneNumber.number : contract.contactPhoneNumber,
                rawPhone: contract.contactPhoneNumber || '',
                communicationLanguage: contract.communicationLanguage,
            });
            setInvoiceData({
                email: contract.invoiceEmailAddress || '',
                vat: contract.vatNumber || '',
                vatCountry: contract.countryCode || '',
                vatList:
                    contract.vatList?.map(v => ({
                        vat: v.vatNumber,
                        country: v.countryCode,
                        permanentEstablishment: v.permanentEstablishment.toString(),
                    })) || [],
                schemeId: contract.eInvoicingId?.split(':')[0] ?? '',
                participantIdentifier: contract.eInvoicingId?.split(':')[1] ?? '',
            });
        }
    }, [contract]);

    const handleSaveChanges = async (
        newContactData: ContactDetailsSchemaType,
        newInvoiceData: InvoiceDetailsSchemaType
    ) => {
        try {
            await updateContractMutation.mutateAsync({
                contact: newContactData,
                invoice: newInvoiceData,
                organizationNumber: contract?.organizationNumber || '',
            });

            setContactData(newContactData);
            setInvoiceData(newInvoiceData);
            contactFormRef.current?.reset();
            invoiceFormRef.current?.reset();

            Notification.success(intl.formatMessage({ id: 'general.saveChanges.successMessage' }));
        } catch {
            Notification.error(intl.formatMessage({ id: 'general.saveChanges.errorMessage' }));
        }
    };

    const handleCancel = useCallback(() => setDialog(true), []);

    const discardChanges = useCallback(() => {
        if (lastTabSelected !== undefined) {
            handleTabChange(lastTabSelected);
            updateTabInUrl(lastTabSelected);
            setLastTabSelected(undefined);
        }

        contactFormRef.current?.reset();
        invoiceFormRef.current?.reset();

        setDialog(false);
    }, [lastTabSelected, handleTabChange, updateTabInUrl]);

    const renderTabContent = (index: number) => {
        switch (index) {
            case 0:
                return <CompanyContractData customerContract={contract} hasWritePermissions={permissions} />;
            case 1:
                return (
                    <ContactDetails
                        contactData={contactData}
                        saveChanges={newContactData => handleSaveChanges(newContactData, invoiceData!)}
                        handleCancel={handleCancel}
                        loading={updateContractMutation.isLoading}
                        ref={contactFormRef}
                        hasWritePermissions={permissions}
                    />
                );
            case 2:
                return (
                    <InvoiceDetails
                        invoiceData={invoiceData}
                        saveChanges={newInvoiceData => handleSaveChanges(contactData!, newInvoiceData)}
                        handleCancel={handleCancel}
                        loading={updateContractMutation.isLoading}
                        ref={invoiceFormRef}
                        hasWritePermissions={permissions}
                    />
                );
            case 3:
                return (
                    <Documents
                        documents={[
                            {
                                title: t('general.pricingList'),
                                url: formatUrl(RIO_PRICE_LIST_URL, userLanguage),
                                documentType: 'pricing_list',
                            },
                            {
                                title: `${MAN_CHARGE_AND_GO} - ${t('general.privacyStatement')}`,
                                url: formatUrl(PRIVACY_POLICY_URL, userLanguage),
                                documentType: 'privacy_policy',
                            },
                            {
                                title: `${TCS} - ${t('general.privacyStatement')}`,
                                url: TCS_PRIVACY_POLICY_URL,
                                documentType: 'tcs_privacy_policy',
                            },
                            {
                                title: t('general.termsAndConditions'),
                                url: account?.termsAndConditions?.url ?? account?.termsAndConditions?.englishUrl,
                                documentType: 'terms_and_conditions',
                            },
                        ]}
                    />
                );
            default:
                return null;
        }
    };

    const setTabChecker = useCallback(
        (newTabIndex: number) => {
            if (contactFormRef.current?.formState?.isDirty || invoiceFormRef.current?.formState?.isDirty) {
                setLastTabSelected(newTabIndex);
                setDialog(true);
            } else {
                updateTabInUrl(newTabIndex);
                handleTabChange(newTabIndex);
            }
        },
        [handleTabChange, updateTabInUrl]
    );

    const renderTabButton = (index: number, icon: string, titleId: string, descriptionId: string) => (
        <div
            className={`display-flex justify-content-start padding-15 rounded margin-right-25 cursor-pointer hover-bg-highlight-lightest ${
                tabSelected === index ? 'bg-highlight-lightest' : ''
            }`}
            onClick={() => setTabChecker(index)}
        >
            <div className='display-flex align-items-center'>
                <span className={`uikit-icon icon-${icon} text-color-primary text-size-h2`} />
            </div>
            <div className='display-flex flex-column padding-left-15'>
                <h5 className='text-size-16 margin-bottom-10 margin-top-0 text-normal'>
                    <FormattedMessage id={titleId} />
                </h5>
                <p className='text-size-12 text-color-dark margin-0'>
                    <FormattedMessage id={descriptionId} />
                </p>
            </div>
        </div>
    );

    return (
        <>
            <div className='width-auto margin-x-auto display-flex justify-content-end margin-bottom-10'>
                <PendingChangesModal show={dialog} setShow={setDialog} discardChanges={discardChanges} />
                <SupportModal />
            </div>
            <div className='padding-x-10 margin-x-auto rounded bg-white padding-y-25 display-flex '>
                <div className='display-flex flex-column flex-basis-33pct' data-testid='charging-contract-tabs'>
                    {renderTabButton(
                        0,
                        'addressbook',
                        'chargingContract.personalInfo.title',
                        'chargingContract.personalInfo.description'
                    )}
                    {renderTabButton(
                        1,
                        'earphone',
                        'chargingContract.contactDetails.title',
                        permissions
                            ? 'chargingContract.contactDetails.description'
                            : 'chargingContract.contactDetails.description.fleetUser'
                    )}
                    {renderTabButton(
                        2,
                        'info-sign',
                        'chargingContract.invoicingDetails.title',
                        permissions
                            ? 'chargingContract.invoicingDetails.description'
                            : 'chargingContract.invoicingDetails.description.fleetUser'
                    )}
                    {renderTabButton(
                        3,
                        'document',
                        'chargingContract.documents.title',
                        'chargingContract.documents.description'
                    )}
                </div>
                <div className='display-flex flex-basis-66pct padding-left-25 border border-left-only border-width-2'>
                    {isContractLoading ? (
                        <div className='display-flex align-items-center justify-content-center width-100pct'>
                            <Spinner />
                        </div>
                    ) : (
                        renderTabContent(tabSelected)
                    )}
                </div>
            </div>
        </>
    );
};

export default ChargingContract;
